// packages/storefront/src/components/general/WidgetLanguageAlert.tsx
import { Stack, Typography } from '@mui/material';
import { AlertBox } from '@tourtask/ui/components/AlertBox';
import type { WidgetName } from '@tourtask/storefront/translation/reportWidgetError';
import type { WidgetLanguageProblem } from '@tourtask/storefront/utils/widgetLanguage';
import {
	GivenLanguageCode,
	SupportedLanguagesTable,
	widgetLanguageNoticeTexts,
} from '@tourtask/storefront/components/general/widgetLanguageNotice';

/**
 * What a widget shows in its place when its host page's `data-lang` names no language TourTask supports
 * (`readWidgetLanguage`): the code as the page wrote it, and every supported code with its language, so whoever set
 * the widget up can correct it at a glance. Naming the supported languages discloses nothing: they are the ones
 * every storefront offers.
 *
 * Deliberately NOT translated, like the widgets' other notices that appear before their translations can load
 * (`WidgetErrorBoundary`, the chat's `ChatConnectionError`): the translations come from the API in the widget's
 * language, and this widget has none. The codes render through `ExactValue`, so a visitor's page translator leaves
 * them as they must be typed; the rest may be translated for its reader. Its texts and table are
 * `widgetLanguageNotice`'s, which the terms widget draws without MUI.
 */
export function WidgetLanguageAlert({ problem, widget }: { problem: WidgetLanguageProblem; widget: WidgetName }) {
	const texts = widgetLanguageNoticeTexts(problem, widget);
	return (
		<AlertBox type="error" mb={0}>
			<Stack spacing={1.5}>
				<Typography variant="body2" sx={{ fontWeight: 'bold' }}>
					{texts.title}
				</Typography>
				{problem.kind === 'unsupported' ? (
					<Typography variant="body2" component="div">
						<GivenLanguageCode given={problem.given} />
					</Typography>
				) : null}
				<Typography variant="body2">{texts.instruction}</Typography>
				<SupportedLanguagesTable />
			</Stack>
		</AlertBox>
	);
}
